import React from "react";

import { formatDistance, haversineMeters } from "../../../utils/orders";
import { Icon } from "./icons";

/**
 * Carte d'approche : le chauffeur au centre, la destination si le serveur
 * l'envoie, un trait entre les deux et la distance arrondie.
 *
 * Le mobile passe par l'API d'images statiques Mapbox ; le web n'a pas de
 * jeton Mapbox. On compose donc les tuiles OpenStreetMap dans un SVG : aucun
 * jeton, aucune dépendance, et chaque sondage de 10 s recentre la carte.
 */

const TILE = 256;
const WIDTH = 320;
const HEIGHT = 200;

const worldX = (lng, z) => ((lng + 180) / 360) * TILE * 2 ** z;
const worldY = (lat, z) => {
  const s = Math.sin((lat * Math.PI) / 180);
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * TILE * 2 ** z;
};

/** Le plus grand zoom qui garde la destination dans le cadre. */
function pickZoom(position, destination) {
  if (!destination) return 16;
  for (let z = 17; z > 10; z -= 1) {
    const dx = Math.abs(worldX(destination.lng, z) - worldX(position.lng, z));
    const dy = Math.abs(worldY(destination.lat, z) - worldY(position.lat, z));
    if (dx < WIDTH / 2 - 24 && dy < HEIGHT / 2 - 40) return z;
  }
  return 11;
}

export default function LiveMap({ position, destination }) {
  const z = pickZoom(position, destination);
  const cx = worldX(position.lng, z);
  const cy = worldY(position.lat, z);
  const left = cx - WIDTH / 2;
  const top = cy - HEIGHT / 2;

  const tiles = [];
  const max = 2 ** z;
  for (let tx = Math.floor(left / TILE); tx <= Math.floor((left + WIDTH) / TILE); tx += 1) {
    for (let ty = Math.floor(top / TILE); ty <= Math.floor((top + HEIGHT) / TILE); ty += 1) {
      if (ty < 0 || ty >= max) continue;
      tiles.push(
        <image
          key={`${tx}-${ty}`}
          href={`https://tile.openstreetmap.org/${z}/${((tx % max) + max) % max}/${ty}.png`}
          x={tx * TILE - left}
          y={ty * TILE - top}
          width={TILE}
          height={TILE}
        />
      );
    }
  }

  const dest = destination
    ? { x: worldX(destination.lng, z) - left, y: worldY(destination.lat, z) - top }
    : null;
  const heading = Number.isFinite(position.heading) ? position.heading : null;

  return (
    <div className="wosh-ord-map">
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="xMidYMid slice">
        {tiles}
        {dest && (
          <>
            <path
              d={`M${WIDTH / 2} ${HEIGHT / 2} L${dest.x} ${dest.y}`}
              stroke="#3D4586"
              strokeWidth="3.5"
              strokeDasharray="7 6"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d={`M${dest.x} ${dest.y - 34} c-8 0 -13 6 -13 13 c0 9 13 21 13 21 s13 -12 13 -21 c0 -7 -5 -13 -13 -13 z`}
              fill="#3D4586"
            />
            <circle cx={dest.x} cy={dest.y - 21} r="4.5" fill="#FFFFFF" />
          </>
        )}
        <circle cx={WIDTH / 2} cy={HEIGHT / 2} r="15" fill="rgba(52,166,172,.22)" />
        <circle cx={WIDTH / 2} cy={HEIGHT / 2} r="9" fill="#34A6AC" stroke="#FFFFFF" strokeWidth="3" />
        {heading !== null && (
          <path
            d={`M${WIDTH / 2} ${HEIGHT / 2 - 6} L${WIDTH / 2 + 4} ${HEIGHT / 2 + 3} L${WIDTH / 2} ${HEIGHT / 2 + 1} L${WIDTH / 2 - 4} ${HEIGHT / 2 + 3} Z`}
            transform={`rotate(${heading} ${WIDTH / 2} ${HEIGHT / 2})`}
            fill="#FFFFFF"
          />
        )}
      </svg>
      {destination && (
        <div className="wosh-ord-map__badge">
          <Icon name="navigate" />
          {formatDistance(haversineMeters(position, destination))}
        </div>
      )}
      <div className="wosh-ord-map__credit">
        ©{" "}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
          OpenStreetMap
        </a>
      </div>
    </div>
  );
}
